<script setup>
import { useApi } from '@provise/composables/api'
import { transformer } from '@provise/lib/transformer'
import { useMessageStack } from '@provise/stores/messagestack'
import { v4 as uuid } from 'uuid'
import { computed, inject, ref, toValue, watch } from 'vue'
import { REPLY_STATES } from '../item.vue'
import { useUserStore } from '@provise/stores/user.js'
import { useToggle } from '@vueuse/core'
import Mod from '@provise/components/mod.vue'
import Create from './step_prepare_to_answer/create.vue'
import { replyMapping } from '@/views/complaints/item.vue'
import Upload from '@/components/upload/upload.vue'

const editmod = ref(null)

const props = defineProps({
  disabled: Boolean,
  modelValue: Object,
})

const model = ref({ complaint: {} })
watch(
  () => props.modelValue,
  mv => (model.value = mv || {}),
  {
    immediate: true,
  }
)
const fileUploadModel = ref([])
const fileUploader = ref(null)

const [, , mergeReply, removeReply] = inject('replies')

const userStore = useUserStore()
const [ed, toggleEd] = useToggle(false)
const emit = defineEmits(['deleted', 'declined', 'approved'])
const complaint = inject('complaint', () => ref({}), true)
const url = `complaints/${model.value.complaint.id}/reply/${model.value.id}`
const replyUploadUrl = `${url}/upload`
const acid = uuid()
const approvable = computed(() => {
  return [REPLY_STATES.STATE_WAIT, REPLY_STATES.STATE_WAIT_COMMERCE].includes(model.value.get_state)
})
const isGenerated = computed(() => model.value.name && model.value.name.length)
const denyHelpText = computed(() => {
  if (!userStore.can(['signater', 'supersignater'])) {
    return
  }
  if ([REPLY_STATES.STATE_DONE, REPLY_STATES.STATE_DENY].includes(model.value.get_state)) {
    return
  }
  return true
})

const denyReplace = computed(() => {
  if (!userStore.can(['signater'])) {
    return true
  }
  return false
})
const { push: notify } = useMessageStack()
const transform = transformer([
  'approval_comment',
  'comment',
  'name',
  ['doc_template', 'id'],
  'doc_text',
  ['uploads_replace','id'],
])
const { refresh: remove } = useApi({
  url,
  model,
  method: 'delete',
  transform,
})
const { refresh: approve } = useApi({
  url: `complaints/${model.value.complaint.id}/reply/${model.value.id}/approve`,
  model,
  transform,
  method: 'post',
})
const { refresh: decline } = useApi({
  url: `complaints/${model.value.complaint.id}/reply/${model.value.id}/decline`,
  model,
  transform,
  method: 'post',
})
const { refresh: save } = useApi({
  url,
  model,
  method: 'put',
  transform,
})
async function rm() {
  if (!confirm('Подтвердите удаление')) return
  await remove()
  notify('Ответ удалён')
  removeReply(toValue(model))
  emit('deleted')
}
async function sv() {
  await save()
  model.value = replyMapping(complaint)(model.value)
  emit('edited')

  if (isGenerated.value) {
    editmod.value.hide()
  } else {
    toggleEd(false)
  }
}
async function apr() {
  await approve()
  notify('Ответ согласован')
  mergeReply(toValue(model))
  emit('approved')
}
async function dcln() {
  await decline()
  notify('Ответ отправлен на доработку')
  mergeReply(toValue(model))
  emit('declined')
}
async function replaceFile() {
  if (!confirm('Подтвердите удаление и замену на новый файл')) return
  await fileUploader.value.upload()
  model.value.uploads_replace = fileUploadModel.value?.[0]
  await sv()
  fileUploadModel.value = []
}
const isDisabled = computed(() =>
  [REPLY_STATES.STATE_DONE, REPLY_STATES.STATE_DENY, REPLY_STATES.STATE_WAIT_COMMERCE].includes(model.value.get_state) ||
  props.disabled
)
</script>
<template>
  <div class="card">
    <div class="card-body">
      <div class="hstack gap-3 mb-3">
        <div class="col-5">
          <a download class="form-control disabled" :href="model.link">
            <i class="bi bi-file-earmark-fill" />
            {{ model.display_name }}
          </a>
        </div>

        <button
          type="button"
          class="btn btn-success"
          @click="toggleEd(true)"
          v-if="isGenerated"
          :disabled="isDisabled"
        >
          <i class="bi bi-pencil" />
        </button>

        <button
          type="button"
          class="btn btn-secondary"
          @click="rm"
          :disabled="isDisabled"
        >
          <i class="bi bi-trash" />
        </button>
        <Upload
          v-if="!isGenerated"
          #="{ choose }"
          :url="replyUploadUrl"
          :delayed="true"
          division="complaint.reply"
          @upload:chosen="replaceFile"
          v-model="fileUploadModel"
          ref="fileUploader"
        >
          <button 
            class="btn btn-secondary" 
            @click="choose"
            :disabled="isDisabled"
          >
            <i class="bi bi-arrow-left-right" />
          </button>
        </Upload>
        <span class="text-muted small" v-if="!model.comment">Комментарий отсутствует</span>
        <span v-else>Комментарий:&nbsp;{{ model.comment }}</span>
      </div>
      <span
        class="text-muted small"
        v-if="model.approval_comment_label"
        v-html="model.approval_comment_label"
      ></span>
      <p></p>
      <label :for="acid" class="form-label" v-if="approvable">Замечания:</label>
      <div class="mb-3">
        <textarea
          class="form-control"
          rows="3"
          v-model="model.approval_comment"
          v-if="approvable"
          :id="acid"
          :disabled="disabled"
        />
        <div class="form-text" v-if="denyHelpText">Ответ без комментария не отклонить</div>
      </div>

      <div v-if="model.get_state === REPLY_STATES.STATE_DONE" class="text-success">
        <i class="bi bi-check-circle-fill" />
        Ответ согласован
      </div>
      <div v-else-if="model.get_state === REPLY_STATES.STATE_DENY" class="text-danger">
        <i class="bi bi-exclamation-circle-fill" />
        Ответ отправлен на доработку
      </div>
      <template v-else>
        <div
          class="hstack gap-3"
          v-if="
            (model.get_state === REPLY_STATES.STATE_WAIT && userStore.can('signater')) ||
            (model.get_state === REPLY_STATES.STATE_WAIT_COMMERCE && userStore.can('supersignater'))
          "
        >
          <button type="button" class="btn btn-success btn-edit" @click="apr" :disabled="disabled">
            <i class="bi bi-check-square" />
            Согласовать ответ
          </button>
          <button
            type="button"
            class="btn btn-danger btn-edit"
            @click="dcln"
            :disabled="disabled || !model.approval_comment?.length"
          >
            <i class="bi bi-x-square" />
            Отправить на доработку
          </button>
        </div>
        <div class="hstack gap-3" v-else-if="model.get_state === REPLY_STATES.STATE_WAIT_COMMERCE">
          <div class="text-warning-emphasis">
            <i class="bi bi-check-circle-fill" />
            Ответ ожидает согласования коммерческого отдела
          </div>
        </div>
        <div class="hstack gap-3" v-else-if="model.get_state === REPLY_STATES.STATE_WAIT">
          <div class="text-warning-emphasis">
            <i class="bi bi-check-circle-fill" />
            Ответ ожидает согласования менеджера отдела обращений
          </div>
        </div>
      </template>
    </div>
  </div>
  <Mod v-if="isGenerated && ed" @hidden="toggleEd(false)" ref="editmod">
    <template #header>Редактируем ответ</template>
    <Create v-model="model" :api-fn="sv" />
  </Mod>
</template>
